import { useState, useCallback, useRef, useEffect } from 'react';
import { Check, Circle, Plus, ChevronDown, ChevronRight, ChevronUp, Trash2, GripVertical } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUpdateJournal } from '@/hooks/useJournals';
import type { TodoItem } from '@/types/journal';
import confetti from 'canvas-confetti';

// Fire confetti from both bottom corners, angled towards center
// Exported so it can be triggered from other components (e.g., when agent marks todo as complete)
export const fireConfetti = () => {
    const defaults = {
        spread: 60,
        ticks: 150,
        gravity: 1,
        decay: 0.94,
        startVelocity: 35,
        colors: ['#10b981', '#34d399', '#6ee7b7', '#fbbf24', '#f59e0b', '#a3e635'],
        scalar: 1.1,
    };

    // Left corner burst - angled right (towards center)
    confetti({
        ...defaults,
        particleCount: 80,
        origin: { x: 0, y: 1 },
        angle: 60, // Angled towards center-right
    });

    // Right corner burst - angled left (towards center)
    confetti({
        ...defaults,
        particleCount: 80,
        origin: { x: 1, y: 1 },
        angle: 120, // Angled towards center-left
    });

    // Second wave for more impact (slightly delayed feeling with different settings)
    setTimeout(() => {
        confetti({
            ...defaults,
            particleCount: 50,
            origin: { x: 0.1, y: 1 },
            angle: 70,
            startVelocity: 45,
        });
        confetti({
            ...defaults,
            particleCount: 50,
            origin: { x: 0.9, y: 1 },
            angle: 110,
            startVelocity: 45,
        });
    }, 100);
};

interface SortableTodoItemProps {
    todo: TodoItem;
    onToggle: (id: string) => void;
    onDelete: (id: string) => void;
    onUpdateText: (id: string, text: string) => void;
}

const SortableTodoItem = ({ todo, onToggle, onDelete, onUpdateText }: SortableTodoItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: todo.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group/item flex items-center gap-2 py-1.5 hover:bg-slate-50/50 rounded-lg transition-colors"
        >
            {/* Drag handle */}
            <div
                {...attributes}
                {...listeners}
                className="flex-shrink-0 cursor-grab active:cursor-grabbing opacity-0 group-hover/item:opacity-100 transition-opacity"
            >
                <GripVertical className="w-3.5 h-3.5 text-slate-300" />
            </div>

            {/* Checkbox */}
            <button
                onClick={() => onToggle(todo.id)}
                className="flex-shrink-0"
            >
                {todo.completed ? (
                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                ) : (
                    <Circle className="w-4 h-4 text-slate-300 hover:text-emerald-400 transition-colors" />
                )}
            </button>

            {/* Text */}
            <input
                type="text"
                value={todo.text}
                onChange={(e) => onUpdateText(todo.id, e.target.value)}
                className={`flex-1 text-sm bg-transparent border-none outline-none ${todo.completed
                    ? 'text-slate-400 line-through'
                    : 'text-slate-700'
                    }`}
            />

            {/* Delete button */}
            <button
                onClick={() => onDelete(todo.id)}
                className="flex-shrink-0 p-1 opacity-0 group-hover/item:opacity-100 text-slate-300 hover:text-red-400 transition-all"
                title="Delete"
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

interface JournalTodoListProps {
    journalId: string;
    initialTodos?: TodoItem[];
    /** Called when expanded state changes */
    onExpandedChange?: (isExpanded: boolean) => void;
    /** When set to true, collapses the Todo list */
    forceCollapse?: boolean;
}

export const JournalTodoList = ({ journalId, initialTodos = [], onExpandedChange, forceCollapse }: JournalTodoListProps) => {
    const [todos, setTodos] = useState<TodoItem[]>(initialTodos);
    const [newTodoText, setNewTodoText] = useState('');
    const [showCompleted, setShowCompleted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isHovered, setIsHovered] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const updateJournal = useUpdateJournal();
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Sync with initial todos when they change (from server)
    useEffect(() => {
        setTodos(initialTodos);
    }, [initialTodos]);

    // Notify parent when expanded state changes
    useEffect(() => {
        onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    // Handle external force collapse (from Collapse All button)
    useEffect(() => {
        if (forceCollapse && isExpanded) {
            setIsExpanded(false);
        }
    }, [forceCollapse]);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Save to database with debounce
    const saveToDatabase = useCallback((newTodos: TodoItem[]) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            updateJournal.mutate({
                journalId,
                updates: { todo_items: newTodos },
            });
        }, 500); // Debounce 500ms
    }, [journalId, updateJournal]);

    // Update todos and save
    const updateTodos = useCallback((newTodos: TodoItem[]) => {
        setTodos(newTodos);
        saveToDatabase(newTodos);
    }, [saveToDatabase]);

    const handleToggle = (id: string) => {
        const todo = todos.find(t => t.id === id);
        const wasCompleted = todo?.completed;

        const updated = todos.map(t =>
            t.id === id ? {
                ...t,
                completed: !t.completed,
                completedAt: !t.completed ? new Date().toISOString() : undefined
            } : t
        );
        updateTodos(updated);

        // Fire confetti when marking as complete (not when unchecking)
        if (!wasCompleted) {
            fireConfetti();
        }
    };

    const handleAddTodo = () => {
        if (!newTodoText.trim()) return;

        const maxOrder = todos.reduce((max, t) => Math.max(max, t.order), -1);
        const newTodo: TodoItem = {
            id: `todo-${Date.now()}`,
            text: newTodoText.trim(),
            completed: false,
            order: maxOrder + 1,
            createdAt: new Date().toISOString(),
        };
        updateTodos([...todos, newTodo]);
        setNewTodoText('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTodo();
        }
    };

    const handleDeleteTodo = (id: string) => {
        const updated = todos.filter(todo => todo.id !== id);
        updateTodos(updated);
    };

    const handleUpdateText = (id: string, text: string) => {
        const updated = todos.map(todo =>
            todo.id === id ? { ...todo, text } : todo
        );
        updateTodos(updated);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const activeTodosList = todos.filter(t => !t.completed);
            const oldIndex = activeTodosList.findIndex(t => t.id === active.id);
            const newIndex = activeTodosList.findIndex(t => t.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedActive = arrayMove(activeTodosList, oldIndex, newIndex);
                // Update order numbers
                const updatedActive = reorderedActive.map((t, i) => ({ ...t, order: i }));
                const completedList = todos.filter(t => t.completed);
                updateTodos([...updatedActive, ...completedList]);
            }
        }
    };

    // Separate active and completed todos
    const activeTodos = todos.filter(t => !t.completed).sort((a, b) => a.order - b.order);
    const completedTodos = todos.filter(t => t.completed);
    const completedCount = completedTodos.length;

    // Preview text for collapsed state
    const previewText = activeTodos.length > 0
        ? `${activeTodos.length} task${activeTodos.length > 1 ? 's' : ''} remaining`
        : completedCount > 0
            ? `${completedCount} completed`
            : 'No tasks';

    return (
        <div
            data-tour="todo-list"
            className="group relative mb-6"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Content with subtle left border like NoteBlock */}
            <div className={`relative border-l-2 border-emerald-200/60 pl-4 transition-all duration-200 ${isHovered ? 'border-emerald-300' : ''
                }`}>
                {/* Click the left border to toggle expand/collapse */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className="absolute left-0 top-0 bottom-0 w-3 bg-transparent cursor-pointer"
                    aria-label={isExpanded ? "Collapse to do list" : "Expand to do list"}
                    title={isExpanded ? "Collapse" : "Expand"}
                />

                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-600">To do</span>
                    {!isExpanded && (
                        <span className="text-xs text-slate-400">{previewText}</span>
                    )}
                </div>

                {isExpanded ? (
                    <div className="space-y-0.5">
                        {/* Completed section - collapsible, at top */}
                        {completedCount > 0 && (
                            <div className="pb-2 mb-2 border-b border-slate-100">
                                <button
                                    onClick={() => setShowCompleted(!showCompleted)}
                                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-500 transition-colors py-1"
                                >
                                    {showCompleted ? (
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    ) : (
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    )}
                                    <span>{completedCount} completed</span>
                                </button>

                                {showCompleted && (
                                    <div className="mt-1 space-y-0.5">
                                        {completedTodos.map((todo) => (
                                            <div
                                                key={todo.id}
                                                className="group/item flex items-center gap-2 py-1.5 hover:bg-slate-50/50 rounded-lg transition-colors pl-5"
                                            >
                                                {/* Checkbox */}
                                                <button
                                                    onClick={() => handleToggle(todo.id)}
                                                    className="flex-shrink-0"
                                                >
                                                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                                                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                                    </div>
                                                </button>

                                                {/* Text */}
                                                <span className="flex-1 text-sm text-slate-400 line-through">
                                                    {todo.text}
                                                </span>

                                                {/* Delete button */}
                                                <button
                                                    onClick={() => handleDeleteTodo(todo.id)}
                                                    className="flex-shrink-0 p-1 opacity-0 group-hover/item:opacity-100 text-slate-300 hover:text-red-400 transition-all"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Active todos */}
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={activeTodos.map(t => t.id)} strategy={verticalListSortingStrategy}>
                                {activeTodos.map((todo) => (
                                    <SortableTodoItem
                                        key={todo.id}
                                        todo={todo}
                                        onToggle={handleToggle}
                                        onDelete={handleDeleteTodo}
                                        onUpdateText={handleUpdateText}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>

                        {/* Add new todo input */}
                        <div className="flex items-center gap-2 py-1.5 pl-5">
                            <Plus className="w-4 h-4 text-slate-300 flex-shrink-0" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={newTodoText}
                                onChange={(e) => setNewTodoText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="What do you want to learn?"
                                className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-300"
                            />
                        </div>
                    </div>
                ) : null}

                {/* Collapse/expand affordance */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-1 mb-2 text-xs text-slate-300 hover:text-slate-400 transition-colors flex items-center gap-1"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="w-3 h-3" />
                            <span>Collapse</span>
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3" />
                            <span>Expand</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
